<template>
  <div class="relative bg-blueGray-50 pt-16 pb-16">
    <div class="create-dashboard-container mx-auto w-full">
      <div class="flex flex-wrap items-center mb-6 gap-3">
        <div class="relative flex-grow">
          <h2 class="text-2xl font-semibold text-blueGray-800">创建数据统计</h2>
          <p class="text-sm text-blueGray-500 mt-1">
            每日活动创建、0参与原因、重复创建和配置试错诊断
          </p>
        </div>
        <el-select v-model="days" size="large" style="width: 140px" @change="loadData">
          <el-option label="最近7天" :value="7" />
          <el-option label="最近14天" :value="14" />
          <el-option label="最近30天" :value="30" />
        </el-select>
        <el-button type="primary" size="large" :loading="loading" @click="loadData">
          刷新
        </el-button>
      </div>

      <div v-loading="loading">
        <el-empty v-if="!latest.task_total && !loading" description="暂无创建数据统计，请先执行每日分析脚本落库" />

        <template v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <metric-card
              title="活动总数"
              description="当天创建的活动总量"
              :value="latest.task_total"
              unit="个"
              tone="blue"
              :trend="metricTrends.taskTotal"
            />
            <metric-card
              title="创建者数"
              description="当天创建活动的去重用户数"
              :value="latest.creator_count"
              unit="人"
              tone="emerald"
              :trend="metricTrends.creatorCount"
            />
            <metric-card
              title="0参与率"
              description="当天创建活动中，最终参与人数为0的活动占比"
              :value="latest.zero_join_rate"
              unit="%"
              tone="amber"
              :trend="metricTrends.zeroJoinRate"
            />
            <metric-card
              title="2h无人参与率"
              description="创建后2小时内仍无人参与的活动占比"
              :value="twoHourNoJoinRate"
              unit="%"
              tone="rose"
              :trend="metricTrends.twoHourNoJoinRate"
            />
          </div>

          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            <div class="xl:col-span-2 bg-white rounded-lg shadow-lg p-5">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-blueGray-800">逐日趋势</h3>
                  <p class="text-xs text-blueGray-400">活动数、创建人数、新老用户人数、0参与率、2h无人参与率</p>
                </div>
                <el-tag type="info">{{ latest.date_text || "-" }}</el-tag>
              </div>
              <div class="relative h-350-px">
                <canvas ref="trendChart"></canvas>
              </div>
            </div>

            <div class="bg-white rounded-lg shadow-lg p-5">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-blueGray-800">核心诊断</h3>
                <p class="text-xs text-blueGray-400">0参与、触达不足、定时未开奖和重复创建异常</p>
              </div>
              <div class="relative h-350-px">
                <canvas ref="coreDiagnosisChart"></canvas>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <data-card title="开奖方式分布">
              <div class="relative mb-4" style="height: 260px">
                <canvas ref="drawTypeChart"></canvas>
              </div>
              <el-table :data="drawTypeRows" size="small" border>
                <el-table-column prop="draw_type_name" label="开奖方式" />
                <el-table-column prop="count" label="活动数" width="120" />
                <el-table-column prop="rate" label="占比" width="120">
                  <template #default="scope">{{ scope.row.rate }}%</template>
                </el-table-column>
              </el-table>
            </data-card>

            <data-card title="新老用户创建表现">
              <div class="relative mb-4" style="height: 260px">
                <canvas ref="ownerSegmentChart"></canvas>
              </div>
              <el-table :data="ownerSegmentRows" size="small" border>
                <el-table-column prop="name" label="用户类型" />
                <el-table-column prop="task_count" label="活动数" width="100" />
                <el-table-column prop="zero_join_rate" label="0参与率" width="110">
                  <template #default="scope">{{ scope.row.zero_join_rate }}%</template>
                </el-table-column>
                <el-table-column prop="avg_join" label="平均参与" width="110" />
              </el-table>
            </data-card>
          </div>

          <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <data-card title="0参与原因拆解">
              <div class="relative mb-4" style="height: 260px">
                <canvas ref="zeroReasonChart"></canvas>
              </div>
              <el-table :data="zeroReasonRows" size="small" border>
                <el-table-column prop="label" label="原因" />
                <el-table-column prop="count" label="数量" width="120" />
                <el-table-column prop="rate" label="占0参与" width="120">
                  <template #default="scope">{{ scope.row.rate }}%</template>
                </el-table-column>
              </el-table>
            </data-card>

            <data-card title="配置有差异字段">
              <div class="relative mb-4" style="height: 260px">
                <canvas ref="changedFieldChart"></canvas>
              </div>
              <el-table :data="changedFieldRows" size="small" border>
                <el-table-column prop="field" label="字段" />
                <el-table-column prop="count" label="用户数" width="120" />
                <el-table-column prop="rate" label="占配置试错" width="130">
                  <template #default="scope">{{ scope.row.rate }}%</template>
                </el-table-column>
              </el-table>
            </data-card>
          </div>

          <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <data-card title="重复创建诊断">
              <div class="relative" style="height: 300px">
                <canvas ref="repeatChart"></canvas>
              </div>
            </data-card>

            <data-card title="已结束活动配置时长">
              <div class="relative" style="height: 300px">
                <canvas ref="durationChart"></canvas>
              </div>
            </data-card>
          </div>

          <data-card title="昨日参与 Top 20 活动">
            <el-empty v-if="!topTasks.length" description="暂无昨日参与活动数据" />
            <el-table v-else :data="topTasks" size="small" border>
              <el-table-column type="index" label="排名" width="70" />
              <el-table-column label="活动 ID" min-width="180">
                <template #default="scope">
                  <el-button
                    type="text"
                    :loading="qrcodeLoading"
                    :disabled="qrcodeLoading"
                    @click="openTaskQrcode(scope.row.task_id)"
                  >{{ scope.row.task_id }}</el-button>
                </template>
              </el-table-column>
              <el-table-column prop="name" label="活动名称" min-width="220" show-overflow-tooltip />
              <el-table-column prop="type_name" label="类型" min-width="120" />
              <el-table-column prop="join_count" label="参与人数" width="110" />
              <el-table-column prop="visit_count" label="访问人数" width="110" />
              <el-table-column prop="new_join_count" label="新参与人数" width="130" />
            </el-table>
          </data-card>

        </template>
      </div>

      <el-dialog title="活动小程序码" v-model="qrcodeVisible" width="360px" @closed="closeTaskQrcode">
        <div v-loading="qrcodeLoading" class="text-center">
          <img
            v-if="qrcodeUrl"
            :src="qrcodeUrl"
            style="width: 300px; height: 300px;"
            alt="活动小程序码"
          >
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import { h } from "vue";
import { luckdrawApi } from "@/api/luckdraw.js";

Chart.register(...registerables);

const OWNER_SEGMENT_NAMES = {
  new: "新用户首次创建",
  old: "老用户创建",
  unknown: "未知创建者",
};

const CHART_COLORS = [
  "#2563eb",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
  "#f97316",
];

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

const MetricCard = {
  props: {
    title: String,
    description: String,
    value: [String, Number],
    unit: String,
    tone: String,
    trend: Object,
  },
  computed: {
    trendDirection() {
      return (this.trend && this.trend.direction) || "flat";
    },
    trendIconStyle() {
      const map = {
        up: {
          background: "#10b981",
          boxShadow: "0 12px 24px rgba(16, 185, 129, 0.28)",
          color: "#ffffff",
        },
        down: {
          background: "#f43f5e",
          boxShadow: "0 12px 24px rgba(244, 63, 94, 0.28)",
          color: "#ffffff",
        },
        flat: {
          background: "#e2e8f0",
          boxShadow: "0 12px 22px rgba(15, 23, 42, 0.12)",
          color: "#64748b",
        },
      };
      return {
        alignItems: "center",
        borderRadius: "9999px",
        display: "flex",
        height: "42px",
        justifyContent: "center",
        width: "42px",
        ...(map[this.trendDirection] || map.flat),
      };
    },
  },
  methods: {
    renderTrendIcon() {
      const direction = (this.trend && this.trend.direction) || "flat";
      const arrowNodes = direction === "flat"
        ? [h("path", { d: "M6 12h12" })]
        : [
          h("path", { d: direction === "up" ? "M12 19V5" : "M12 5v14" }),
          h("path", { d: direction === "up" ? "M5 12l7-7 7 7" : "M5 12l7 7 7-7" }),
        ];
      return h("svg", {
        fill: "none",
        height: "22",
        stroke: "currentColor",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        "stroke-width": "2.5",
        viewBox: "0 0 24 24",
        width: "22",
      }, arrowNodes);
    },
  },
  render() {
    return h("div", { class: "bg-white rounded-lg shadow-lg p-5" }, [
      h("div", { class: "flex items-center justify-between" }, [
        h("div", { class: "pr-4" }, [
          h("p", { class: "text-xs font-bold uppercase text-blueGray-400" }, this.title),
          h("p", { class: "text-xs text-blueGray-400 mt-1 leading-relaxed" }, this.description),
          h("p", { class: "text-2xl font-semibold text-blueGray-800 mt-2" }, [
            this.value || 0,
            h("span", { class: "text-sm ml-1 text-blueGray-400" }, this.unit),
          ]),
        ]),
        h("div", { class: "flex flex-col items-center flex-shrink-0" }, [
          h("div", { style: this.trendIconStyle }, [
            this.renderTrendIcon(),
          ]),
          h("span", { class: "text-xs text-blueGray-400 mt-2 whitespace-nowrap" }, (this.trend && this.trend.text) || "暂无前日"),
        ]),
      ]),
    ]);
  },
};

const DataCard = {
  props: {
    title: String,
  },
  render() {
    return h("div", { class: "bg-white rounded-lg shadow-lg p-5" }, [
      h("h3", { class: "text-lg font-semibold text-blueGray-800 mb-4" }, this.title),
      this.$slots.default ? this.$slots.default() : null,
    ]);
  },
};

export default {
  name: "CreateDashboard",
  components: {
    MetricCard,
    DataCard,
  },
  data() {
    return {
      days: 14,
      loading: false,
      items: [],
      latest: {},
      trend: {},
      charts: {},
      qrcodeVisible: false,
      qrcodeLoading: false,
      qrcodeUrl: "",
    };
  },
  computed: {
    twoHourNoJoinRate() {
      return (this.latest.two_hour_no_join || {}).rate || 0;
    },
    zeroReason() {
      return this.latest.zero_join_reason || {};
    },
    timedOpen() {
      return this.latest.timed_zero_join_open_timing || {};
    },
    repeatDiagnosis() {
      return this.latest.repeat_create_diagnosis || {};
    },
    ownerSegmentRows() {
      const segments = this.latest.owner_segment_distribution || {};
      return ["new", "old", "unknown"].map((key) => ({
        name: OWNER_SEGMENT_NAMES[key],
        ...(segments[key] || {}),
      }));
    },
    zeroReasonRows() {
      return [
        {
          label: "无非发起人访问且0参与",
          count: this.zeroReason.no_non_owner_visit_zero_join_count || 0,
          rate: this.zeroReason.no_non_owner_visit_zero_join_rate || 0,
        },
        {
          label: "有非发起人访问但0参与",
          count: this.zeroReason.non_owner_visited_zero_join_count || 0,
          rate: this.zeroReason.non_owner_visited_zero_join_rate || 0,
        },
      ];
    },
    drawTypeRows() {
      return safeArray(this.latest.draw_type_distribution);
    },
    changedFieldRows() {
      return safeArray(this.repeatDiagnosis.changed_config_field_distribution);
    },
    durationRows() {
      return safeArray(this.latest.ended_duration_distribution);
    },
    topTasks() {
      return safeArray(this.latest.top_tasks);
    },
    metricTrends() {
      const previous = this.items.length >= 2 ? this.items[this.items.length - 2] : null;
      return {
        taskTotal: this.buildMetricTrend(this.latest.task_total, previous && previous.task_total, "个"),
        creatorCount: this.buildMetricTrend(this.latest.creator_count, previous && previous.creator_count, "人"),
        zeroJoinRate: this.buildMetricTrend(this.latest.zero_join_rate, previous && previous.zero_join_rate, "pp"),
        twoHourNoJoinRate: this.buildMetricTrend(
          this.twoHourNoJoinRate,
          previous && previous.two_hour_no_join && previous.two_hour_no_join.rate,
          "pp"
        ),
      };
    },
  },
  mounted() {
    this.loadData();
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    async openTaskQrcode(taskId) {
      if (!taskId) {
        return;
      }
      this.qrcodeLoading = true;
      this.qrcodeUrl = "";
      try {
        const result = await luckdrawApi.qrcode({ id: taskId });
        if (!result || result.sta !== 0 || !result.data || !result.data.promotion_wxa_qrcode_url) {
          throw new Error((result && result.msg) || "获取活动小程序码失败");
        }
        this.qrcodeUrl = result.data.promotion_wxa_qrcode_url;
        this.qrcodeVisible = true;
      } catch (error) {
        this.$message.error(error.message || "获取活动小程序码失败");
      } finally {
        this.qrcodeLoading = false;
      }
    },
    closeTaskQrcode() {
      this.qrcodeVisible = false;
      this.qrcodeUrl = "";
    },
    async loadData() {
      this.loading = true;
      try {
        const result = await luckdrawApi.getDailyCreateDashboard({ days: this.days });
        if (result && result.sta === 0 && result.data) {
          this.items = result.data.items || [];
          this.latest = result.data.latest || {};
          this.trend = result.data.trend || {};
        } else {
          this.$message.error((result && result.msg) || "加载创建数据统计失败");
        }
      } catch (error) {
        console.error("加载创建数据统计失败:", error);
        this.$message.error("加载创建数据统计失败");
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          requestAnimationFrame(() => this.renderCharts());
        });
      }
    },
    destroyChart() {
      Object.values(this.charts).forEach((chart) => chart.destroy());
      this.charts = {};
    },
    renderCharts() {
      this.destroyChart();
      [
        this.renderTrendChart,
        this.renderCoreDiagnosisChart,
        this.renderDrawTypeChart,
        this.renderOwnerSegmentChart,
        this.renderZeroReasonChart,
        this.renderChangedFieldChart,
        this.renderRepeatChart,
        this.renderDurationChart,
      ].forEach((renderChart) => {
        try {
          renderChart.call(this);
        } catch (error) {
          console.error("创建数据统计图表渲染失败:", error);
        }
      });
    },
    createChart(refName, config) {
      const canvas = this.$refs[refName];
      if (!canvas) {
        return;
      }
      const parent = canvas.parentElement;
      if (parent && (!parent.clientWidth || !parent.clientHeight)) {
        requestAnimationFrame(() => this.createChart(refName, config));
        return;
      }
      this.charts[refName] = new Chart(canvas, config);
    },
    buildMetricTrend(current, previous, unit) {
      if (previous === null || previous === undefined) {
        return { direction: "flat", text: "暂无前日" };
      }
      const diff = Number(current || 0) - Number(previous || 0);
      const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
      const sign = diff > 0 ? "+" : "";
      const value = unit === "pp" ? `${sign}${diff.toFixed(1)}pp` : `${sign}${diff}${unit}`;
      return {
        direction,
        text: direction === "flat" ? "较昨日持平" : `较昨日 ${value}`,
      };
    },
    renderTrendChart() {
      if (!this.$refs.trendChart || !this.trend.labels) {
        return;
      }
      this.createChart("trendChart", {
        type: "line",
        data: {
          labels: this.trend.labels,
          datasets: [
            {
              label: "活动数",
              data: this.trend.task_total || [],
              borderColor: "#2563eb",
              backgroundColor: "#2563eb",
              yAxisID: "count",
              tension: 0.35,
            },
            {
              label: "创建人数",
              data: this.trend.creator_count || [],
              borderColor: "#10b981",
              backgroundColor: "#10b981",
              yAxisID: "count",
              borderDash: [6, 4],
              tension: 0.35,
            },
            {
              label: "新用户人数",
              data: this.trend.new_creator_count || [],
              borderColor: "#06b6d4",
              backgroundColor: "#06b6d4",
              yAxisID: "count",
              borderDash: [3, 4],
              tension: 0.35,
            },
            {
              label: "老用户人数",
              data: this.trend.old_creator_count || [],
              borderColor: "#84cc16",
              backgroundColor: "#84cc16",
              yAxisID: "count",
              borderDash: [3, 4],
              tension: 0.35,
            },
            {
              label: "0参与率",
              data: this.trend.zero_join_rate || [],
              borderColor: "#f59e0b",
              backgroundColor: "#f59e0b",
              yAxisID: "rate",
              tension: 0.35,
            },
            {
              label: "2h无人参与率",
              data: this.trend.two_hour_no_join_rate || [],
              borderColor: "#e11d48",
              backgroundColor: "#e11d48",
              yAxisID: "rate",
              tension: 0.35,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: {
              position: "bottom",
            },
          },
          scales: {
            count: {
              type: "linear",
              position: "left",
              title: { display: true, text: "数量" },
              beginAtZero: true,
            },
            rate: {
              type: "linear",
              position: "right",
              title: { display: true, text: "比例" },
              beginAtZero: true,
              grid: { drawOnChartArea: false },
              ticks: {
                callback: (value) => `${value}%`,
              },
            },
          },
        },
      });
    },
    renderCoreDiagnosisChart() {
      if (!this.$refs.coreDiagnosisChart || !this.items.length) {
        return;
      }
      const labels = this.items.map((item) => item.date_label || item.date_text || "");
      const getZeroReason = (item) => item.zero_join_reason || {};
      const getTimedOpen = (item) => item.timed_zero_join_open_timing || {};
      const getRepeatDiagnosis = (item) => item.repeat_create_diagnosis || {};
      this.createChart("coreDiagnosisChart", {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "0参与活动",
              data: this.items.map((item) => item.zero_join_count || 0),
              borderColor: "#f59e0b",
              backgroundColor: "#f59e0b",
              tension: 0.35,
            },
            {
              label: "无非发起人访问且0参与",
              data: this.items.map((item) => getZeroReason(item).no_non_owner_visit_zero_join_count || 0),
              borderColor: "#ef4444",
              backgroundColor: "#ef4444",
              tension: 0.35,
            },
            {
              label: "定时未开奖未触达",
              data: this.items.map((item) => getTimedOpen(item).open_not_due_count || 0),
              borderColor: "#8b5cf6",
              backgroundColor: "#8b5cf6",
              tension: 0.35,
            },
            {
              label: "短间隔异常重复用户",
              data: this.items.map((item) => getRepeatDiagnosis(item).suspicious_short_interval_count || 0),
              borderColor: "#06b6d4",
              backgroundColor: "#06b6d4",
              tension: 0.35,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: {
              position: "bottom",
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
              },
            },
          },
        },
      });
    },
    renderDrawTypeChart() {
      const rows = this.drawTypeRows;
      this.createChart("drawTypeChart", {
        type: "doughnut",
        data: {
          labels: rows.map((item) => item.draw_type_name),
          datasets: [{
            data: rows.map((item) => item.count),
            backgroundColor: CHART_COLORS,
            borderWidth: 0,
          }],
        },
        options: this.getBaseChartOptions("bottom"),
      });
    },
    renderOwnerSegmentChart() {
      const rows = this.ownerSegmentRows;
      this.createChart("ownerSegmentChart", {
        type: "bar",
        data: {
          labels: rows.map((item) => item.name),
          datasets: [
            {
              label: "活动数",
              data: rows.map((item) => item.task_count || 0),
              backgroundColor: "#2563eb",
              yAxisID: "count",
            },
            {
              label: "0参与率",
              data: rows.map((item) => item.zero_join_rate || 0),
              backgroundColor: "#f59e0b",
              yAxisID: "rate",
            },
          ],
        },
        options: this.getCountRateOptions(),
      });
    },
    renderZeroReasonChart() {
      const rows = this.zeroReasonRows;
      this.createChart("zeroReasonChart", {
        type: "doughnut",
        data: {
          labels: rows.map((item) => item.label),
          datasets: [{
            data: rows.map((item) => item.count),
            backgroundColor: ["#ef4444", "#10b981"],
            borderWidth: 0,
          }],
        },
        options: this.getBaseChartOptions("bottom"),
      });
    },
    renderChangedFieldChart() {
      const rows = this.changedFieldRows.slice(0, 8);
      this.createChart("changedFieldChart", {
        type: "bar",
        data: {
          labels: rows.map((item) => item.field),
          datasets: [{
            label: "用户数",
            data: rows.map((item) => item.count),
            backgroundColor: "#8b5cf6",
          }],
        },
        options: this.getHorizontalBarOptions(),
      });
    },
    renderRepeatChart() {
      const diagnosis = this.repeatDiagnosis;
      const rows = [
        { label: "重复创建者", value: diagnosis.repeat_creator_count || 0 },
        { label: "短间隔异常", value: diagnosis.suspicious_short_interval_count || 0 },
        { label: "重复提交", value: diagnosis.same_config_repeat_count || 0 },
        { label: "配置试错", value: diagnosis.changed_config_repeat_count || 0 },
        { label: "失败后重建", value: diagnosis.short_interval_failed_recreate_count || 0 },
      ];
      this.createChart("repeatChart", {
        type: "bar",
        data: {
          labels: rows.map((item) => item.label),
          datasets: [{
            label: "用户数",
            data: rows.map((item) => item.value),
            backgroundColor: ["#2563eb", "#f97316", "#ef4444", "#8b5cf6", "#10b981"],
          }],
        },
        options: this.getVerticalBarOptions(),
      });
    },
    renderDurationChart() {
      const rows = this.durationRows;
      this.createChart("durationChart", {
        type: "bar",
        data: {
          labels: rows.map((item) => item.duration_bucket),
          datasets: [{
            label: "活动数",
            data: rows.map((item) => item.count),
            backgroundColor: "#06b6d4",
          }],
        },
        options: this.getVerticalBarOptions(),
      });
    },
    getBaseChartOptions(legendPosition = "bottom") {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: legendPosition,
          },
        },
      };
    },
    getVerticalBarOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
            },
          },
        },
      };
    },
    getHorizontalBarOptions() {
      return {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              precision: 0,
            },
          },
        },
      };
    },
    getCountRateOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
        },
        scales: {
          count: {
            type: "linear",
            position: "left",
            beginAtZero: true,
            title: { display: true, text: "活动数" },
          },
          rate: {
            type: "linear",
            position: "right",
            beginAtZero: true,
            title: { display: true, text: "0参与率" },
            grid: { drawOnChartArea: false },
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.create-dashboard-container {
  max-width: 1480px;
  padding-left: 16px;
  padding-right: 16px;
}

@media (min-width: 768px) {
  .create-dashboard-container {
    padding-left: 32px;
    padding-right: 32px;
  }
}

@media (min-width: 1280px) {
  .create-dashboard-container {
    padding-left: 48px;
    padding-right: 48px;
  }
}
</style>
